/**
 * Minimal RFC 5545 RRULE expander for MVP (Phase 2 Wave 4).
 *
 * Supports:
 *   FREQ=DAILY
 *   FREQ=WEEKLY [BYDAY=MO,TU,WE,TH,FR,SA,SU]
 *   FREQ=MONTHLY   (same day-of-month as master.starts_at)
 *   FREQ=YEARLY    (same month/day-of-month as master.starts_at)
 *
 * Hard-coded INTERVAL=1 for MVP. Anything more complex (BYMONTHDAY numeric
 * lists, EXDATE, INTERVAL>1, BYSETPOS, …) is deferred to a future wave and
 * simply treated as a single-occurrence event (the master itself if it falls
 * inside the range).
 *
 * Caller responsibility:
 *   - Pass the master event's starts_at / ends_at as absolute Date objects
 *     (UTC recommended; the expander does not perform DST transitions).
 *   - Pass rangeStart / rangeEnd as inclusive boundaries (UTC).
 *   - Read masterEvent.recurrence_until (optional) to cap expansion.
 *
 * The expander returns an array of { starts_at, ends_at } pairs ordered by
 * starts_at ascending. Each pair preserves the master's duration.
 *
 * Spec: specs/CODEX_PHASE2_WAVE4_CALENDAR_TASKS_HOME.md §1.1 + §2.7
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export type RecurrenceByDay = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export interface ParsedRRule {
  freq: RecurrenceFreq;
  /** Optional BYDAY for weekly expansion. Empty array means "no BYDAY". */
  byDay: RecurrenceByDay[];
}

export interface RecurrenceMasterEvent {
  /** Master event starts_at. */
  starts_at: Date | string;
  /** Master event ends_at. */
  ends_at: Date | string;
  /** RFC 5545 RRULE body, e.g. `FREQ=WEEKLY;BYDAY=MO,WE`. null/undefined → single instance. */
  recurrence_rule?: string | null;
  /** Optional hard stop; any occurrence with starts_at > recurrence_until is dropped. */
  recurrence_until?: Date | string | null;
}

export interface RecurrenceOccurrence {
  starts_at: Date;
  ends_at: Date;
}

// ---------------------------------------------------------------------------
// Hard caps — defence against runaway expansion in production.
// ---------------------------------------------------------------------------

/** Max occurrences the expander will emit for any single call. */
export const MAX_OCCURRENCES = 1000;

/** Max iterations before we bail out regardless of MAX_OCCURRENCES. */
const MAX_ITERATIONS = 5000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDate(v: Date | string): Date {
  return v instanceof Date ? new Date(v.getTime()) : new Date(v);
}

function toDateOrNull(v: Date | string | null | undefined): Date | null {
  if (v === null || v === undefined) return null;
  const d = toDate(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** JS Sunday=0..Saturday=6 → RRULE MO,TU,WE,TH,FR,SA,SU. */
const JS_DOW_TO_RRULE: Record<number, RecurrenceByDay> = {
  0: 'SU',
  1: 'MO',
  2: 'TU',
  3: 'WE',
  4: 'TH',
  5: 'FR',
  6: 'SA',
};

const RRULE_DOW_TO_JS: Record<RecurrenceByDay, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

const VALID_BYDAY = new Set<string>(Object.keys(RRULE_DOW_TO_JS));

// ---------------------------------------------------------------------------
// RRULE parser (tolerant — unknown parts are ignored)
// ---------------------------------------------------------------------------

export function parseRRule(rule: string | null | undefined): ParsedRRule | null {
  if (!rule) return null;
  const trimmed = rule.trim();
  if (!trimmed) return null;

  // Accept an optional leading "RRULE:" prefix per RFC 5545.
  const body = trimmed.replace(/^RRULE:/i, '');

  const parts = body.split(';');
  const kv: Record<string, string> = {};
  for (const p of parts) {
    const eq = p.indexOf('=');
    if (eq <= 0) continue;
    const key = p.slice(0, eq).trim().toUpperCase();
    const value = p.slice(eq + 1).trim();
    if (!key || !value) continue;
    kv[key] = value;
  }

  const freqRaw = kv.FREQ;
  if (!freqRaw) return null;
  const freq = freqRaw.toUpperCase();
  if (freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY' && freq !== 'YEARLY') {
    return null;
  }

  let byDay: RecurrenceByDay[] = [];
  if (kv.BYDAY) {
    byDay = kv.BYDAY.split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s): s is RecurrenceByDay => VALID_BYDAY.has(s));
  }

  return { freq: freq as RecurrenceFreq, byDay };
}

// ---------------------------------------------------------------------------
// Month-end helper — advance a Date by N months, clamping the day.
// ---------------------------------------------------------------------------
//
// Example: Jan 31 + 1 month → Feb 28 (or Feb 29 on leap years).
// Example: Aug 31 + 1 month → Sep 30.
//
// We preserve hour/minute/second/ms and use UTC math so DST never affects
// the arithmetic.

function addMonthsUtc(base: Date, months: number): Date {
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth();
  const d = base.getUTCDate();
  const h = base.getUTCHours();
  const mi = base.getUTCMinutes();
  const s = base.getUTCSeconds();
  const ms = base.getUTCMilliseconds();

  const targetYear = y + Math.floor((m + months) / 12);
  const targetMonthRaw = (((m + months) % 12) + 12) % 12;
  // Last day of target month: construct day 0 of the NEXT month.
  const lastDay = new Date(Date.UTC(targetYear, targetMonthRaw + 1, 0)).getUTCDate();
  const clampedDay = Math.min(d, lastDay);
  return new Date(Date.UTC(targetYear, targetMonthRaw, clampedDay, h, mi, s, ms));
}

function addYearsUtc(base: Date, years: number): Date {
  // Add 12 * years months so the leap-year Feb 29 clamp still applies:
  // Feb 29 2024 + 1 year → Feb 28 2025.
  return addMonthsUtc(base, years * 12);
}

function addDaysUtc(base: Date, days: number): Date {
  const d = new Date(base.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

// ---------------------------------------------------------------------------
// Main expander
// ---------------------------------------------------------------------------

export function expandRecurrence(
  masterEvent: RecurrenceMasterEvent,
  rangeStart: Date | string,
  rangeEnd: Date | string,
): RecurrenceOccurrence[] {
  const startAt = toDate(masterEvent.starts_at);
  const endAt = toDate(masterEvent.ends_at);
  const rStart = toDate(rangeStart);
  const rEnd = toDate(rangeEnd);

  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
    return [];
  }
  if (Number.isNaN(rStart.getTime()) || Number.isNaN(rEnd.getTime())) {
    return [];
  }
  if (rEnd.getTime() < rStart.getTime()) return [];

  const duration = endAt.getTime() - startAt.getTime();
  const recurUntil = toDateOrNull(masterEvent.recurrence_until);

  const parsed = parseRRule(masterEvent.recurrence_rule);

  // No (or unparseable) rule → treat as a single instance.
  if (!parsed) {
    if (startAt.getTime() > rEnd.getTime()) return [];
    if (endAt.getTime() < rStart.getTime()) return [];
    return [{ starts_at: new Date(startAt.getTime()), ends_at: new Date(endAt.getTime()) }];
  }

  const out: RecurrenceOccurrence[] = [];

  // Effective upper bound for iteration.
  const hardStop = recurUntil ? new Date(Math.min(rEnd.getTime(), recurUntil.getTime())) : rEnd;

  // Utility: include an occurrence if it overlaps [rStart, rEnd].
  const pushIfInRange = (occStart: Date) => {
    const occEnd = new Date(occStart.getTime() + duration);
    if (occEnd.getTime() < rStart.getTime()) return;
    if (occStart.getTime() > hardStop.getTime()) return;
    out.push({ starts_at: occStart, ends_at: occEnd });
  };

  if (parsed.freq === 'DAILY') {
    let cur = new Date(startAt.getTime());
    // Fast-forward to the first date on or after (rStart - duration).
    // For MVP correctness we just iterate from master.starts_at; MAX_ITERATIONS
    // guards against pathological ranges.
    let iterations = 0;
    while (
      cur.getTime() <= hardStop.getTime() &&
      out.length < MAX_OCCURRENCES &&
      iterations < MAX_ITERATIONS
    ) {
      pushIfInRange(new Date(cur.getTime()));
      cur = addDaysUtc(cur, 1);
      iterations += 1;
    }
    return out;
  }

  if (parsed.freq === 'WEEKLY') {
    // If BYDAY is empty, recur on the master's own weekday, once per week.
    const fallbackWeekday = JS_DOW_TO_RRULE[startAt.getUTCDay()] ?? 'SU';
    const weekdays: RecurrenceByDay[] = parsed.byDay.length > 0 ? parsed.byDay : [fallbackWeekday];

    const targetDows = new Set(weekdays.map((d) => RRULE_DOW_TO_JS[d]));
    let cur = new Date(startAt.getTime());

    let iterations = 0;
    while (
      cur.getTime() <= hardStop.getTime() &&
      out.length < MAX_OCCURRENCES &&
      iterations < MAX_ITERATIONS
    ) {
      if (targetDows.has(cur.getUTCDay())) {
        pushIfInRange(new Date(cur.getTime()));
      }
      cur = addDaysUtc(cur, 1);
      iterations += 1;
    }
    return out;
  }

  if (parsed.freq === 'MONTHLY') {
    let cur = new Date(startAt.getTime());
    let iterations = 0;
    while (
      cur.getTime() <= hardStop.getTime() &&
      out.length < MAX_OCCURRENCES &&
      iterations < MAX_ITERATIONS
    ) {
      pushIfInRange(new Date(cur.getTime()));
      cur = addMonthsUtc(startAt, iterations + 1);
      iterations += 1;
    }
    return out;
  }

  if (parsed.freq === 'YEARLY') {
    let cur = new Date(startAt.getTime());
    let iterations = 0;
    while (
      cur.getTime() <= hardStop.getTime() &&
      out.length < MAX_OCCURRENCES &&
      iterations < MAX_ITERATIONS
    ) {
      pushIfInRange(new Date(cur.getTime()));
      cur = addYearsUtc(startAt, iterations + 1);
      iterations += 1;
    }
    return out;
  }

  return out;
}
